/**
 * AdminBusinessCategories — CRUD for marketplace verticals + their
 * subcategories. Parent rows can be expanded to manage subcategories
 * inline.
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, X, Loader2, Pencil, Trash2, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import BizCategoryIcon from "../../components/BizCategoryIcon";

const ICON_CHOICES = [
    "UtensilsCrossed", "Drumstick", "ShoppingCart", "ShoppingBag", "Pill",
    "Sparkles", "Baby", "PawPrint", "Wrench", "WashingMachine", "HeartPulse",
    "Car", "Dumbbell", "GraduationCap", "Whistle", "PartyPopper", "Building2",
    "Banknote", "Tractor", "Grid",
];

const EMPTY = { name: "", slug: "", icon: "Grid", parent_id: null };

export default function AdminBusinessCategories() {
    const [rows, setRows] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [modal, setModal] = useState(null); // { mode: "create"|"edit", parent_id, row? }
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/admin/business-categories").then(({ data }) => setRows(data || []));
    useEffect(() => { load(); }, []);

    const parents = useMemo(() => rows.filter((r) => !r.parent_id), [rows]);
    const subsBy = useMemo(() => {
        const m = {};
        for (const r of rows) if (r.parent_id) (m[r.parent_id] = m[r.parent_id] || []).push(r);
        return m;
    }, [rows]);

    const openCreate = (parent_id = null) => {
        setForm({ ...EMPTY, parent_id });
        setModal({ mode: "create", parent_id });
    };
    const openEdit = (row) => {
        setForm({ name: row.name, slug: row.slug, icon: row.icon || "Grid", parent_id: row.parent_id || null });
        setModal({ mode: "edit", row });
    };

    const save = async () => {
        setBusy(true);
        try {
            if (modal.mode === "create") {
                await api.post("/admin/business-categories", form);
                toast.success("Category created");
            } else {
                await api.patch(`/admin/business-categories/${modal.row.id}`, form);
                toast.success("Category updated");
            }
            setModal(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    const remove = async (row) => {
        if (!confirm(`Disable "${row.name}"?${row.parent_id ? "" : "\nAll its subcategories will be disabled too."}`)) return;
        await api.delete(`/admin/business-categories/${row.id}`);
        toast.success("Disabled");
        load();
    };

    const restore = async (row) => {
        try {
            await api.post(`/admin/business-categories/${row.id}/restore`);
            toast.success(`"${row.name}" re-activated`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed");
        }
    };

    return (
        <div data-testid="admin-business-categories">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Business Categories</h1>
                    <p className="text-sm text-muted-foreground mt-1">{parents.length} verticals · {rows.length - parents.length} subcategories</p>
                </div>
                <button data-testid="add-biz-cat" onClick={() => openCreate(null)} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> Add Category</button>
            </div>

            <div className="mt-6 bg-card border border-border rounded-2xl divide-y divide-border">
                {parents.map((p) => {
                    const subs = subsBy[p.id] || [];
                    const open = !!expanded[p.id];
                    return (
                        <div key={p.id} data-testid={`biz-cat-${p.slug}`}>
                            <div className="flex items-center gap-3 p-4">
                                <button onClick={() => setExpanded((s) => ({ ...s, [p.id]: !open }))} className="p-1 -ml-1 text-muted-foreground">
                                    {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                </button>
                                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                                    <BizCategoryIcon name={p.icon} size={20} strokeWidth={2.4} className="text-amber-700" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold flex items-center gap-2">
                                        {p.name}
                                        {p.status !== "ACTIVE" && <span className="text-[10px] uppercase font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Inactive</span>}
                                    </div>
                                    <div className="text-xs text-muted-foreground">/{p.slug} · {subs.length} subcategor{subs.length === 1 ? "y" : "ies"}</div>
                                </div>
                                <button data-testid={`biz-cat-add-sub-${p.slug}`} onClick={() => openCreate(p.id)} className="text-xs font-semibold text-primary px-3 py-1.5 rounded-lg hover:bg-primary/10 inline-flex items-center gap-1"><Plus size={13} /> Subcategory</button>
                                <button onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-muted"><Pencil size={14} /></button>
                                {p.status === "ACTIVE" ? (
                                    <button onClick={() => remove(p)} data-testid={`biz-cat-disable-${p.slug}`} className="p-2 rounded-lg text-destructive hover:bg-destructive/10"><Trash2 size={14} /></button>
                                ) : (
                                    <button onClick={() => restore(p)} data-testid={`biz-cat-restore-${p.slug}`} title="Re-activate" className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"><RotateCcw size={14} /></button>
                                )}
                            </div>
                            {open && subs.length > 0 && (
                                <div className="bg-muted/30 px-4 py-2 pl-16 space-y-1">
                                    {subs.map((s) => (
                                        <div key={s.id} className="flex items-center gap-2 py-1.5" data-testid={`biz-sub-${s.slug}`}>
                                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                                            <span className="text-sm font-medium flex-1">{s.name} <span className="text-xs text-muted-foreground">/{s.slug}</span></span>
                                            {s.status !== "ACTIVE" && <span className="text-[10px] uppercase font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Inactive</span>}
                                            <button onClick={() => openEdit(s)} className="p-1.5 rounded-lg hover:bg-card"><Pencil size={13} /></button>
                                            {s.status === "ACTIVE" ? (
                                                <button onClick={() => remove(s)} data-testid={`biz-sub-disable-${s.slug}`} className="p-1.5 rounded-lg text-destructive hover:bg-destructive/10"><Trash2 size={13} /></button>
                                            ) : (
                                                <button onClick={() => restore(s)} data-testid={`biz-sub-restore-${s.slug}`} title="Re-activate" className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"><RotateCcw size={13} /></button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {modal && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setModal(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-2xl w-full max-w-md p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-bold text-lg">{modal.mode === "create" ? (modal.parent_id ? "New Subcategory" : "New Category") : "Edit"}</h2>
                            <button onClick={() => setModal(null)} className="p-1.5 rounded-lg hover:bg-muted"><X size={16} /></button>
                        </div>
                        <div className="space-y-3">
                            <label className="block">
                                <span className="text-xs font-semibold text-muted-foreground">Name</span>
                                <input data-testid="biz-cat-name" autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="block w-full bg-muted rounded-lg px-3 py-2 text-sm mt-1" />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-muted-foreground">Slug (optional — auto from name)</span>
                                <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="e.g. pet-care" className="block w-full bg-muted rounded-lg px-3 py-2 text-sm mt-1" />
                            </label>
                            {/* Icons are only meaningful for top-level verticals — subcategories
                                are pure text labels under their parent. */}
                            {!form.parent_id && (
                                <div>
                                    <span className="text-xs font-semibold text-muted-foreground">Icon</span>
                                    <div className="grid grid-cols-7 gap-1.5 mt-1 max-h-44 overflow-y-auto p-2 bg-muted rounded-lg">
                                        {ICON_CHOICES.map((icn) => (
                                            <button
                                                key={icn}
                                                type="button"
                                                data-testid={`biz-cat-icon-${icn}`}
                                                onClick={() => setForm({ ...form, icon: icn })}
                                                className={`p-2 rounded-lg flex items-center justify-center ${form.icon === icn ? "bg-primary text-primary-foreground" : "bg-card hover:bg-card/70"}`}
                                                title={icn}
                                            >
                                                <BizCategoryIcon name={icn} size={18} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <button data-testid="biz-cat-save" disabled={busy || !form.name.trim()} onClick={save} className="mt-5 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                            {busy && <Loader2 size={16} className="animate-spin" />} Save
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
